import { PlusIcon, UploadSimpleIcon } from "@phosphor-icons/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { useDeleteMqttSource } from "@/api/reseller-manager/mutations";
import { useRmMySources } from "@/api/reseller-manager/queries";
import type { MqttSourceRM } from "@/api/reseller-manager/types";
import {
	type Column,
	MeterTable,
	usePagination,
} from "@/components/meters/meter-table";
import { CopyableCell } from "@/components/shared/copyable-cell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyCell } from "@/components/ui/empty-cell";
import { isValidLang, type Lang, ROUTE_SLUGS } from "@/config/routes";
import { useDebounce } from "@/hooks/use-debounce";
import { AssignToResellerDialog } from "./assign-to-reseller-dialog";
import { CreateSourceDialog } from "./create-source-dialog";
import { CsvUploadDialog } from "./csv-upload-dialog";
import { FilterlistUploadDialog } from "./filterlist-upload-dialog";
import { GatewaySearchCombobox } from "./gateway-search-combobox";
import { SourceBulkActions } from "./source-bulk-actions";
import { SourceRowActions } from "./source-row-actions";

export function RmMqttTable() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { lang } = useParams<{ lang: string }>();
	const currentLang: Lang = lang && isValidLang(lang) ? lang : "pl";
	const { page, setPage } = usePagination();
	const [pageSize, setPageSize] = useState(20);
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [showAssign, setShowAssign] = useState(false);
	const [assignSns, setAssignSns] = useState<string[]>([]);
	const [filterlistSn, setFilterlistSn] = useState("");
	const [createSourceOpen, setCreateSourceOpen] = useState(false);
	const [csvUploadOpen, setCsvUploadOpen] = useState(false);
	const [searchSn, setSearchSn] = useState("");
	const [comboQuery, setComboQuery] = useState("");
	const debouncedComboQuery = useDebounce(comboQuery);

	const deleteMutation = useDeleteMqttSource();
	const [ordering, setOrdering] = useState("");
	const { data, isLoading } = useRmMySources({
		page,
		page_size: pageSize,
		search: searchSn || undefined,
		ordering: ordering || undefined,
	});
	const comboSearch = useRmMySources({
		page_size: 20,
		search: debouncedComboQuery || undefined,
	});
	const comboOptions = (comboSearch.data?.results ?? []).map((s) => ({
		sn: s.sn,
		label: s.owner?.company_name,
	}));
	const handleComboSearch = useCallback((q: string) => setComboQuery(q), []);
	const rows = data?.results ?? [];
	const selectedSns = Array.from(selected);

	const isAllSelected =
		rows.length > 0 && rows.every((r) => selected.has(r.sn));
	const isIndeterminate =
		!isAllSelected && rows.some((r) => selected.has(r.sn));

	function toggleKey(sn: string) {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(sn)) next.delete(sn);
			else next.add(sn);
			return next;
		});
	}

	function toggleAll() {
		setSelected(isAllSelected ? new Set() : new Set(rows.map((r) => r.sn)));
	}

	function handleDelete(sn: string) {
		deleteMutation.mutate(sn, {
			onSuccess: () => toast.success(t("mqttSources.deleted")),
			onError: () => toast.error(t("mqttSources.deleteError")),
		});
	}

	function handleBulkDelete() {
		Promise.all(
			selectedSns.map(
				(sn) =>
					new Promise<string>((resolve, reject) =>
						deleteMutation.mutate(sn, {
							onSuccess: () => resolve(sn),
							onError: () => reject(sn),
						}),
					),
			),
		)
			.then(() => {
				toast.success(t("mqttSources.bulkDeleted"));
				setSelected(new Set());
			})
			.catch(() => toast.error(t("mqttSources.deleteError")));
	}

	function openAssign(sns: string[]) {
		setAssignSns(sns);
		setShowAssign(true);
	}

	const columns: Column<MqttSourceRM>[] = [
		{
			key: "sn",
			label: t("mqttSources.colSerialNumber"),
			sortField: "sn",
			render: (row) => (
				<CopyableCell value={row.sn} label={t("mqttSources.colSerialNumber")} />
			),
		},
		{
			key: "owner",
			label: t("mqttSources.colReseller"),
			render: (row) =>
				row.owner?.company_name ?? row.owner?.email ?? <EmptyCell />,
		},
		{
			key: "district",
			label: t("mqttSources.colDistrict"),
			render: (row) => row.district_name ?? <EmptyCell />,
		},
		{
			key: "meters_count",
			label: t("mqttSources.colMeters"),
			render: (row) => row.meters_count,
		},
		{
			key: "_details",
			label: t("mqttSources.colStatus"),
			render: (row) =>
				row.owner != null ? (
					<Badge variant="energy">{t("mqttSources.statusAssigned")}</Badge>
				) : (
					<Badge variant="warning">{t("mqttSources.statusUnassigned")}</Badge>
				),
		},
		{
			key: "_actions",
			label: "",
			render: (row) => (
				<SourceRowActions
					readingsDisabled={row.district == null}
					isDeleting={deleteMutation.isPending}
					onReadings={() =>
						navigate(
							`/${currentLang}/${ROUTE_SLUGS.readings[currentLang]}?search=${row.sn}`,
						)
					}
					onAssign={() => openAssign([row.sn])}
					onUploadFilterlist={() => setFilterlistSn(row.sn)}
					onDelete={() => handleDelete(row.sn)}
				/>
			),
		},
	];

	const toolbarActions = (
		<div className="flex w-full flex-wrap items-center gap-2">
			<GatewaySearchCombobox
				value={searchSn}
				onChange={(sn) => {
					setSearchSn(sn);
					setPage(1);
				}}
				options={comboOptions}
				isLoading={comboSearch.isLoading}
				onSearch={handleComboSearch}
			/>
			<div className="ml-auto flex items-center gap-2">
				<Button onClick={() => setCreateSourceOpen(true)}>
					<PlusIcon aria-hidden="true" />
					{t("createSource.button")}
				</Button>
				<Button variant="outline" onClick={() => setCsvUploadOpen(true)}>
					<UploadSimpleIcon aria-hidden="true" />
					{t("csvUpload.button")}
				</Button>
			</div>
		</div>
	);

	return (
		<>
			<MeterTable
				data={data}
				columns={columns}
				loading={isLoading}
				page={page}
				pageSize={pageSize}
				onPageChange={setPage}
				onPageSizeChange={(size) => {
					setPageSize(size);
					setPage(1);
				}}
				headerActions={toolbarActions}
				selectionBar={
					<SourceBulkActions
						selectedSns={selectedSns}
						isPending={deleteMutation.isPending}
						onAssign={() => openAssign(selectedSns)}
						onDelete={handleBulkDelete}
						onClear={() => setSelected(new Set())}
					/>
				}
				ordering={ordering}
				onOrderingChange={(val) => {
					setOrdering(val);
					setPage(1);
				}}
				rowKeyField="sn"
				selectedKeys={selectedSns}
				onSelectKey={toggleKey}
				onSelectAll={toggleAll}
				isAllSelected={isAllSelected}
				isIndeterminate={isIndeterminate}
			/>
			<AssignToResellerDialog
				open={showAssign}
				onOpenChange={(o) => {
					if (!o) {
						setShowAssign(false);
						setAssignSns([]);
						setSelected(new Set());
					}
				}}
				selectedSns={assignSns}
			/>
			<FilterlistUploadDialog
				open={filterlistSn !== ""}
				onOpenChange={(o) => {
					if (!o) setFilterlistSn("");
				}}
				gatewaySn={filterlistSn}
			/>
			<CreateSourceDialog
				open={createSourceOpen}
				onOpenChange={setCreateSourceOpen}
			/>
			<CsvUploadDialog open={csvUploadOpen} onOpenChange={setCsvUploadOpen} />
		</>
	);
}
